import React from 'react';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Crown } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

/**
 * Popup for upgrade-gated features. Use it as a controlled dialog whenever
 * a user tries to trigger something that requires a higher plan.
 *
 * Example:
 *   const [open, setOpen] = useState(false);
 *   <UpgradeGatePopup
 *     open={open}
 *     onOpenChange={setOpen}
 *     featureTitle="Pressemitteilung automatisch versenden"
 *     featureDescription="Sollen wir automatisch aussenden, damit du in 8 Suchmaschinen und 40 Presseportalen erscheinst?"
 *     requiredPlan="growth"
 *   />
 */
export const UpgradeGatePopup = ({
  open,
  onOpenChange,
  featureTitle,
  featureDescription,
  requiredPlan = 'growth',
}) => {
  const { lang } = useLanguage();
  const sub = useSubscription();
  const navigate = useNavigate();

  // Fetch real plan limits/features from the API so this stays in sync with admin changes.
  const [planDoc, setPlanDoc] = useState(null);
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/plans/all`);
        const list = Array.isArray(r.data) ? r.data : [];
        const found = list.find(p => (p.name || '').toLowerCase() === String(requiredPlan).toLowerCase());
        setPlanDoc(found || null);
      } catch { setPlanDoc(null); }
    })();
  }, [open, requiredPlan]);

  const planLabel = planDoc?.name || (requiredPlan === 'growth' ? 'Growth' : 'Starter');
  const limits = planDoc?.limits || {};
  const features = planDoc?.features || {};
  const fmt = (n) => Number(n || 0).toLocaleString(lang === 'de' ? 'de-DE' : 'en-US');
  const alreadyEligible =
    sub.plan === requiredPlan || sub.plan === 'custom' || sub.plan === 'unlimited';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0f1f36] border-[#1A2B42] text-white max-w-md"
        data-testid="upgrade-gate-popup"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2 text-amber-400">
            <Crown className="w-5 h-5" />
            <span className="text-xs uppercase tracking-wide font-semibold">
              {planLabel} {lang === 'de' ? 'Feature' : 'feature'}
            </span>
          </div>
          <DialogTitle className="text-xl">{featureTitle}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-[#A0AEC0] leading-relaxed">
          {featureDescription}
        </p>

        <div className="mt-4 rounded-lg border border-[#1A2B42] bg-[#0A1628]/60 p-3 text-xs text-[#A0AEC0]">
          <div className="flex items-center gap-2 text-[#00D9C8] font-medium mb-1">
            <Sparkles className="w-4 h-4" />
            {lang === 'de' ? `Enthalten im ${planLabel}-Plan:` : `Included in ${planLabel}:`}
          </div>
          {planDoc ? (
            <ul className="space-y-0.5 list-disc list-inside">
              <li>{lang === 'de' ? `${fmt(limits.credits_monthly)} Credits / Monat` : `${fmt(limits.credits_monthly)} credits / month`}</li>
              <li>{lang === 'de' ? `${limits.connected_channels_social || 0} Social Channel${limits.connected_channels_social === 1 ? '' : 's'}` : `${limits.connected_channels_social || 0} social channel${limits.connected_channels_social === 1 ? '' : 's'}`}</li>
              {(limits.connected_channels_website || 0) > 0 && (
                <li>{lang === 'de' ? `${limits.connected_channels_website} Webseite${limits.connected_channels_website === 1 ? '' : 'n'}` : `${limits.connected_channels_website} website${limits.connected_channels_website === 1 ? '' : 's'}`}</li>
              )}
              {features.video_generation && <li>{lang === 'de' ? 'Video-Generierung' : 'Video generation'}</li>}
              {features.press_release_distribution && <li>{lang === 'de' ? 'Presseverteiler' : 'Press distribution'}</li>}
              {features.priority_support && <li>{lang === 'de' ? 'Priority Support' : 'Priority support'}</li>}
            </ul>
          ) : (
            <p className="text-[#A0AEC0]">{lang === 'de' ? 'Lade Plan-Details…' : 'Loading plan details…'}</p>
          )}
        </div>

        <DialogFooter className="mt-5 flex flex-row gap-2 sm:justify-end">
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="upgrade-gate-cancel"
            className="text-[#A0AEC0]"
          >
            {lang === 'de' ? 'Abbrechen' : 'Cancel'}
          </Button>
          <Button
            data-testid="upgrade-gate-cta"
            disabled={alreadyEligible}
            onClick={() => { onOpenChange(false); navigate('/app/upgrade'); }}
            className="bg-[#00D9C8] hover:bg-[#00BFB0] text-[#0A1628] font-semibold"
          >
            {alreadyEligible
              ? (lang === 'de' ? 'Bereits aktiv' : 'Already active')
              : (lang === 'de' ? 'Jetzt upgraden' : 'Upgrade now')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UpgradeGatePopup;
